import {
  initialMemoryStats,
  initialWordStats,
  mockQuestionnaire,
} from '../mock/questionnaire'
import type {
  ExperienceSubmission,
  Questionnaire,
  RelationStat,
  WordCloudDimension,
  WordStat,
} from '../types/questionnaire'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false'
export const isMockMode = USE_MOCK
const PENDING_SUBMISSIONS_KEY = 'taste-memory:pending-submissions'
const QUESTIONNAIRE_CACHE_KEY = 'taste-memory:questionnaire-cache'
const WORD_STATS_STORAGE_KEYS: Record<WordCloudDimension, string> = {
  taste: 'taste-memory:word-stats',
  memory_category: 'taste-memory:memory-stats',
}
const REAL_WORD_STATS_CACHE_KEYS: Record<WordCloudDimension, string> = {
  taste: 'taste-memory:api-word-stats',
  memory_category: 'taste-memory:api-memory-stats',
}
const MOCK_RELATIONS_STORAGE_KEY = 'taste-memory:relations'

const INITIAL_STATS: Record<WordCloudDimension, WordStat[]> = {
  taste: initialWordStats,
  memory_category: initialMemoryStats,
}

function delay(duration = 180) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, duration))
}

class ApiRequestError extends Error {
  constructor(readonly status: number) {
    super(`Request failed: ${status}`)
    this.name = 'ApiRequestError'
  }
}

export interface SubmissionResult {
  queued: boolean
}

function readStoredJson<T>(key: string): T | null {
  const raw = window.localStorage.getItem(key)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function writeStoredJson(key: string, value: unknown) {
  window.localStorage.setItem(key, JSON.stringify(value))
}

function isRetryableRequestError(error: unknown) {
  if (error instanceof ApiRequestError) {
    return error.status === 408 || error.status === 429 || error.status >= 500
  }

  return error instanceof TypeError
}

function readPendingSubmissions(): ExperienceSubmission[] {
  const stored = readStoredJson<unknown>(PENDING_SUBMISSIONS_KEY)
  if (!Array.isArray(stored)) {
    return []
  }

  return stored.filter((item): item is ExperienceSubmission => {
    if (!item || typeof item !== 'object') {
      return false
    }

    const candidate = item as Partial<ExperienceSubmission>
    return typeof candidate.submissionId === 'string' && Array.isArray(candidate.answers)
  })
}

function enqueueSubmission(payload: ExperienceSubmission) {
  try {
    const pending = readPendingSubmissions()
    if (!pending.some((item) => item.submissionId === payload.submissionId)) {
      pending.push(payload)
      writeStoredJson(PENDING_SUBMISSIONS_KEY, pending)
    }
    return true
  } catch {
    return false
  }
}

function readMockStats(dimension: WordCloudDimension): WordStat[] {
  const raw = window.localStorage.getItem(WORD_STATS_STORAGE_KEYS[dimension])

  if (!raw) {
    return INITIAL_STATS[dimension].map((item) => ({ ...item }))
  }

  try {
    return JSON.parse(raw) as WordStat[]
  } catch {
    return INITIAL_STATS[dimension].map((item) => ({ ...item }))
  }
}

function writeMockStats(dimension: WordCloudDimension, stats: WordStat[]) {
  window.localStorage.setItem(WORD_STATS_STORAGE_KEYS[dimension], JSON.stringify(stats))
}

export function normalizeCustomTaste(value: string) {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
}

export function customTasteStatCode(value: string) {
  const normalized = normalizeCustomTaste(value)
  return normalized ? `custom:${normalized}` : ''
}

export function customMemoryStatCode(value: string) {
  const normalized = normalizeCustomTaste(value)
  return normalized ? `custom:${normalized}` : ''
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })

  if (!response.ok) {
    throw new ApiRequestError(response.status)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const responseText = await response.text()
  return (responseText ? JSON.parse(responseText) : undefined) as T
}

export async function getActiveQuestionnaire(): Promise<Questionnaire> {
  if (USE_MOCK) {
    await delay()
    return mockQuestionnaire
  }

  try {
    const questionnaire = await request<Questionnaire>('/public/questionnaires/active')
    writeStoredJson(QUESTIONNAIRE_CACHE_KEY, questionnaire)
    return questionnaire
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    return readStoredJson<Questionnaire>(QUESTIONNAIRE_CACHE_KEY) || mockQuestionnaire
  }
}

export async function getWordStats(dimension: WordCloudDimension): Promise<WordStat[]> {
  if (USE_MOCK) {
    await delay(120)
    return readMockStats(dimension)
  }

  try {
    const stats = await request<WordStat[]>(`/public/stats/word-cloud?dimension=${dimension}`)
    writeStoredJson(REAL_WORD_STATS_CACHE_KEYS[dimension], stats)
    return stats
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    const cachedStats = readStoredJson<WordStat[]>(REAL_WORD_STATS_CACHE_KEYS[dimension])
    if (cachedStats) {
      return cachedStats
    }

    throw error
  }
}

export async function getTasteRelations(tasteCode: string, limit = 5): Promise<RelationStat[]> {
  if (USE_MOCK) {
    await delay(100)
    const relations = readStoredJson<RelationStat[]>(MOCK_RELATIONS_STORAGE_KEY) || []
    return relations
      .filter((item) => item.sourceCode === tasteCode)
      .sort((left, right) => right.count - left.count)
      .slice(0, limit)
  }

  const cacheKey = `taste-memory:api-relations:${tasteCode}`
  try {
    const relations = await request<RelationStat[]>(
      `/public/stats/relations?tasteCode=${encodeURIComponent(tasteCode)}&limit=${limit}`,
    )
    writeStoredJson(cacheKey, relations)
    return relations
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    const cachedRelations = readStoredJson<RelationStat[]>(cacheKey)
    if (cachedRelations) {
      return cachedRelations
    }
    throw error
  }
}

async function sendSubmission(payload: ExperienceSubmission) {
  await request<void>('/public/submissions', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

let pendingSync: Promise<number> | null = null

export function syncPendingSubmissions(): Promise<number> {
  if (USE_MOCK) {
    return Promise.resolve(0)
  }

  if (pendingSync) {
    return pendingSync
  }

  pendingSync = (async () => {
    const pending = readPendingSubmissions()
    let syncedCount = 0

    for (let index = 0; index < pending.length; index += 1) {
      try {
        await sendSubmission(pending[index])
        syncedCount += 1
      } catch {
        writeStoredJson(PENDING_SUBMISSIONS_KEY, pending.slice(index))
        return syncedCount
      }
    }

    if (pending.length > 0) {
      window.localStorage.removeItem(PENDING_SUBMISSIONS_KEY)
    }
    return syncedCount
  })().finally(() => {
    pendingSync = null
  })

  return pendingSync
}

export async function submitExperience(payload: ExperienceSubmission): Promise<SubmissionResult> {
  if (!USE_MOCK) {
    try {
      await sendSubmission(payload)
      return { queued: false }
    } catch (error) {
      if (isRetryableRequestError(error) && enqueueSubmission(payload)) {
        return { queued: true }
      }
      throw error
    }
  }

  await delay(260)

  const tasteAnswer = payload.answers.find((answer) => answer.questionCode === 'taste')
  const tasteCode = tasteAnswer?.optionCode === 'custom'
    ? customTasteStatCode(tasteAnswer.textValue || '')
    : tasteAnswer?.optionCode || ''

  if (tasteCode) {
    const tasteStats = readMockStats('taste')
    const target = tasteStats.find((item) => item.code === tasteCode)

    if (target) {
      target.count += 1
    } else if (tasteAnswer?.optionCode === 'custom') {
      tasteStats.push({
        code: tasteCode,
        label: normalizeCustomTaste(tasteAnswer.textValue || ''),
        count: 1,
      })
    }

    writeMockStats('taste', tasteStats)
  }

  const memoryAnswer = payload.answers.find((answer) => answer.questionCode === 'memory_category')
  const memoryCode = memoryAnswer?.optionCode === 'custom'
    ? customMemoryStatCode(memoryAnswer.textValue || '')
    : memoryAnswer?.optionCode || ''
  if (memoryCode) {
    const memoryStats = readMockStats('memory_category')
    const target = memoryStats.find((item) => item.code === memoryCode)
    if (target) {
      target.count += 1
    } else if (memoryAnswer?.optionCode === 'custom') {
      memoryStats.push({
        code: memoryCode,
        label: normalizeCustomTaste(memoryAnswer.textValue || ''),
        count: 1,
      })
    }
    writeMockStats('memory_category', memoryStats)
  }

  if (tasteCode && memoryCode) {
    const relations = readStoredJson<RelationStat[]>(MOCK_RELATIONS_STORAGE_KEY) || []
    const target = relations.find((item) => (
      item.sourceCode === tasteCode && item.targetCode === memoryCode
    ))

    if (target) {
      target.count += 1
    } else {
      const tasteLabel = readMockStats('taste').find((item) => item.code === tasteCode)?.label || tasteCode
      const memoryLabel = readMockStats('memory_category')
        .find((item) => item.code === memoryCode)?.label || memoryCode
      relations.push({
        sourceCode: tasteCode,
        sourceLabel: tasteLabel,
        targetCode: memoryCode,
        targetLabel: memoryLabel,
        count: 1,
      })
    }
    writeStoredJson(MOCK_RELATIONS_STORAGE_KEY, relations)
  }

  return { queued: false }
}
